'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Star, Calendar, AlertCircle, Monitor, Download } from 'lucide-react';
// import { useRouter } from 'next/navigation';
import { getImageUrl, IMAGE_SIZES } from '../lib/tmdb';
import { getApiUrl } from '../lib/api';
import type { TMDBMovie } from '../lib/tmdb';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { useIsTVDevice } from '../hooks/useTVNavigation';
import { getStreamSources } from '../lib/streamSources';

// YouTube IFrame API types
declare global {
  interface Window {
    YT: {
      Player: new (elementId: string, config: any) => any;
      PlayerState: {
        ENDED: number;
        PLAYING: number;
        PAUSED: number;
        BUFFERING: number;
        CUED: number;
      };
    };
    onYouTubeIframeAPIReady: () => void;
  }
}

interface Movie {
  id: string;
  title: string;
  size: string;
  quality: string;
  year: string;
  format: string;
  cached?: boolean;
  cacheInfo?: {
    gdriveFileId: string;
    uploadedAt: number;
    accessCount: number;
  } | null;
}

interface MovieModalProps {
  movie: TMDBMovie | null;
  isOpen: boolean;
  onClose: () => void;
  onSearchBot: (movieTitle: string) => Promise<Movie[]>;
  onSelectVersion: (movie: Movie) => void;
  onMovieClick?: (movie: TMDBMovie) => void;
}

export default function MovieModal({
  movie,
  isOpen,
  onClose,
  onSearchBot,
  onSelectVersion,
  onMovieClick,
}: MovieModalProps) {
  const [isSearching, setIsSearching] = useState(false);
  const [botResults, setBotResults] = useState<Movie[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [similarMovies, setSimilarMovies] = useState<TMDBMovie[]>([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [trailerVideoId, setTrailerVideoId] = useState<string | null>(null);
  const [showTrailer, setShowTrailer] = useState(false);
  const [isTrailerLoading, setIsTrailerLoading] = useState(false);
  const [backdropError, setBackdropError] = useState(false);
  const [posterError, setPosterError] = useState(false);
  const [trailerPlayError, setTrailerPlayError] = useState(false);
  const [youtubeApiLoaded, setYoutubeApiLoaded] = useState(false);
  
  // Tab state for Stream vs Download sources
  const [activeTab, setActiveTab] = useState<'stream' | 'download'>('stream');
  
  // Loading state for stream navigation
  const [isNavigatingToStream, setIsNavigatingToStream] = useState(false);
  
  // VidSrc availability check
  const [vidsrcAvailable, setVidsrcAvailable] = useState<boolean | null>(null);
  
  // TV support
  const isTV = useIsTVDevice();
  const router = { push: (path: string) => console.log('Navigate to:', path) };
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const youtubePlayerRef = useRef<any>(null);
  const previousMovieIdRef = useRef<number | undefined>(undefined);

  // Memoize movie ID and title to prevent unnecessary re-renders
  const movieId = useMemo(() => movie?.id, [movie?.id]);
  const movieTitle = useMemo(() => movie?.title, [movie?.title]);

  // Handle search bot function - stabilized with memoized title
  const handleSearchBot = useCallback(async () => {
    if (!movieTitle) return;
    
    setIsSearching(true);
    setHasSearched(true);
    setSearchError(null);
    try {
      const results = await onSearchBot(movieTitle);
      setBotResults(results);
      if (results.length === 0) {
        setSearchError('No versions found for this movie. Try a different search term in the header.');
      }
    } catch (error) {
      console.error('Error searching bot:', error);
      setSearchError('Failed to search for movie versions. Please try again.');
      setBotResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [movieTitle, onSearchBot]);

  // Fetch similar movies function
  const fetchSimilarMovies = useCallback(async (id: number) => {
    setLoadingSimilar(true);
    try {
      const response = await fetch(getApiUrl('TMDB_SIMILAR', undefined, { movieId: id.toString() }));
      const data = await response.json();
      if (data.success) {
        setSimilarMovies(data.results.slice(0, 12));
      }
    } catch (error) {
      console.error('Error fetching similar movies:', error);
    } finally {
      setLoadingSimilar(false);
    }
  }, []);

  // Fetch trailer function - removed movie?.title dependency (only uses movieId)
  const fetchTrailer = useCallback(async (movieId: number) => {
    console.log('🎬 Modal: Fetching trailer for movie ID:', movieId);
    try {
      const response = await fetch(getApiUrl('TMDB_VIDEOS', undefined, { movieId: movieId.toString() }));
      const data = await response.json();
      console.log('🎬 Modal: Trailer API response:', data);
      if (data.success && data.videoId) {
        console.log('✅ Modal: Setting trailer video ID:', data.videoId);
        setTrailerVideoId(data.videoId);
      } else {
        console.log('❌ Modal: No trailer found for movie:', movieId);
        setTrailerVideoId(null);
      }
    } catch (error) {
      console.error('❌ Modal: Error fetching trailer:', error);
      setTrailerVideoId(null);
    }
  }, []);

  // YouTube Player event handlers
  const handlePlayerReady = useCallback((event: any) => {
    console.log('🎬 Modal YouTube player ready!', {
      playerId: event.target.getVideoData().video_id,
      duration: event.target.getDuration()
    });
    youtubePlayerRef.current = event.target;
    setIsTrailerLoading(false);
    setTrailerPlayError(false);
  }, []);

  const handlePlayerStateChange = useCallback((event: any) => {
    console.log('🎬 Modal player state changed:', event.data, {
      state: event.data === window.YT.PlayerState.ENDED ? 'ENDED' : 
             event.data === window.YT.PlayerState.PLAYING ? 'PLAYING' :
             event.data === window.YT.PlayerState.PAUSED ? 'PAUSED' : 'OTHER'
    });
    
    // If video ended, restart it (loop)
    if (event.data === window.YT.PlayerState.ENDED) {
      console.log('🎬 Modal video ended, restarting...');
      if (youtubePlayerRef.current) {
        youtubePlayerRef.current.seekTo(0);
        youtubePlayerRef.current.playVideo();
      }
    }
  }, []);

  const handlePlayerError = useCallback((event: any) => {
    console.error('🎬 Modal YouTube player error:', event.data);
    setTrailerPlayError(true);
    setIsTrailerLoading(false);
    setShowTrailer(false);
  }, []);

  // Handle stream source navigation
  const handleStreamSourceClick = useCallback((sourceId: string) => {
    if (!movie) return;
    
    const streamSources = getStreamSources();
    const source = streamSources.find(s => s.id === sourceId);
    if (!source) return;
    
    // Show loading state
    setIsNavigatingToStream(true);
    
    // Navigate to watch page with all movie metadata for watch tracking
    const params = new URLSearchParams({
      source: sourceId,
      title: movie.title,
      posterPath: movie.poster_path || '',
      backdropPath: movie.backdrop_path || '',
      overview: movie.overview || '',
      releaseDate: movie.release_date || '',
      voteAverage: movie.vote_average?.toString() || ''
    });
    
    const watchUrl = `/watch/${movie.id}?${params.toString()}`;
    window.location.href = watchUrl;
    
    // Keep modal open with loading state for longer
    // Don't close modal automatically - let the new page take over
  }, [movie]);

  // Reset all state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setBotResults([]);
      setHasSearched(false);
      setSearchError(null);
      setSimilarMovies([]);
      setIsSearching(false);
      setLoadingSimilar(false);
      setTrailerVideoId(null);
      setShowTrailer(false);
      setIsTrailerLoading(false);
      setBackdropError(false);
      setPosterError(false);
      setActiveTab('stream'); // Reset to stream tab
      setIsNavigatingToStream(false); // Reset navigation loading
      previousMovieIdRef.current = undefined; // Reset ref
    }
  }, [isOpen]);

  // Check VidSrc availability when movie changes
  useEffect(() => {
    if (isOpen && movieId) {
      const checkVidsrcAvailability = async () => {
        setVidsrcAvailable(null); // Reset to loading state
        
        try {
          const response = await fetch(getApiUrl('VIDSRC_CHECK', movieId.toString()));
          const data = await response.json();
          
          if (data.success) {
            setVidsrcAvailable(data.available);
            console.log(`✅ VidSrc availability for ${movieId}: ${data.available ? 'Available' : 'Not available'}`);
          } else {
            setVidsrcAvailable(false);
            console.warn(`⚠️ VidSrc check failed for ${movieId}, hiding option`);
          }
        } catch (error) {
          console.error('❌ Error checking VidSrc availability:', error);
          setVidsrcAvailable(false); // Hide on error
        }
      };
      
      checkVidsrcAvailability();
    } else {
      setVidsrcAvailable(null);
    }
  }, [isOpen, movieId]);

  // Handle movie changes - reset state and trigger searches
  // Only trigger when movieId actually changes (not when movie object reference changes)
  useEffect(() => {
    // Only run if movieId actually changed from previous value
    if (isOpen && movieId && movieId !== previousMovieIdRef.current) {
      console.log('🎬 Modal: Movie changed, resetting state and fetching data', {
        movieId,
        movieTitle: movieTitle,
        isOpen,
        previousMovieId: previousMovieIdRef.current
      });
      
      // Update ref to track current movieId
      previousMovieIdRef.current = movieId;
      
      // Reset state for new movie
      setBotResults([]);
      setHasSearched(false);
      setSearchError(null);
      setSimilarMovies([]);
      setTrailerVideoId(null);
      setShowTrailer(false);
      setIsTrailerLoading(false);
      setBackdropError(false);
      setPosterError(false);
      
      fetchTrailer(movieId);
      fetchSimilarMovies(movieId);
    }
  }, [isOpen, movieId, movieTitle, handleSearchBot, fetchSimilarMovies, fetchTrailer]);

  // Handle escape/back key and TV focus trap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      // Handle Escape/Back button
      if (e.key === 'Escape' || e.key === 'Back' || e.key === 'XF86Back') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }

        // TV-specific focus trap
        if (isTV && modalRef.current) {
          const modal = modalRef.current;
          const focusableElements = modal.querySelectorAll(
            'button:not([disabled]), [tabindex="0"], [role="button"][tabindex="0"]'
          );
          const focusableArray = Array.from(focusableElements) as HTMLElement[];
          const firstFocusable = focusableArray[0];
          const lastFocusable = focusableArray[focusableArray.length - 1];
          const activeElement = document.activeElement as HTMLElement;
          const currentIndex = focusableArray.indexOf(activeElement);

        // Handle Up/Down navigation within modal
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          e.stopPropagation();
          
          if (currentIndex === -1) {
            // No element focused, focus first element
            firstFocusable?.focus();
          } else if (currentIndex < focusableArray.length - 1) {
            // Focus next element
            focusableArray[currentIndex + 1]?.focus();
          }
          
          // Scroll the modal content down
          if (scrollContainerRef.current) {
            const scrollAmount = 120; // Slightly larger scroll for better visibility
            const currentScrollTop = scrollContainerRef.current.scrollTop;
            const scrollHeight = scrollContainerRef.current.scrollHeight;
            const clientHeight = scrollContainerRef.current.clientHeight;
            
            console.log('🔽 Down arrow - Scroll info:', {
              currentScrollTop,
              scrollHeight,
              clientHeight,
              canScrollDown: currentScrollTop < scrollHeight - clientHeight
            });
            
            scrollContainerRef.current.scrollBy({ top: scrollAmount, behavior: 'smooth' });
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          e.stopPropagation();
          
          // Scroll first, then manage focus
          if (scrollContainerRef.current) {
            const scrollAmount = 120;
            const currentScrollTop = scrollContainerRef.current.scrollTop;
            const scrollHeight = scrollContainerRef.current.scrollHeight;
            const clientHeight = scrollContainerRef.current.clientHeight;
            
            console.log('🔝 Up arrow - Scroll info:', {
              currentScrollTop,
              scrollHeight,
              clientHeight,
              canScrollUp: currentScrollTop > 0
            });
            
            // Always scroll up, even if at boundaries
            const newScrollTop = Math.max(0, currentScrollTop - scrollAmount);
            scrollContainerRef.current.scrollTop = newScrollTop; // Direct assignment for immediate effect
          }
          
          // Then manage focus
          if (currentIndex > 0) {
            focusableArray[currentIndex - 1]?.focus();
          } else if (currentIndex === 0) {
            firstFocusable?.focus();
          }
        }

        // Handle Left/Right navigation for similar movies grid
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          // Check if we're in the "More Like This" section
          const moreLikeThisSection = activeElement.closest('[data-similar-movies]');
          if (moreLikeThisSection) {
            e.preventDefault();
            e.stopPropagation();
            
            if (e.key === 'ArrowLeft' && currentIndex > 0) {
              focusableArray[currentIndex - 1]?.focus();
            } else if (e.key === 'ArrowRight' && currentIndex < focusableArray.length - 1) {
              focusableArray[currentIndex + 1]?.focus();
            }
          }
        }

        // Handle Enter key for YouTube player controls
        if (e.key === 'Enter' || e.key === ' ') {
          // Check if the active element is the YouTube player or inside it
          const youtubePlayerElement = activeElement.closest('[id^="youtube-player-modal-"]');
          
          if (youtubePlayerElement) {
            e.preventDefault();
            e.stopPropagation();
            
            console.log('🎬 TV: YouTube player focused, controls handled by YouTube API');
            // YouTube IFrame API handles its own controls, no need for manual intervention
          }
        }
      }
    };

    // Handle wheel events to prevent background scrolling
    const handleWheel = (e: WheelEvent) => {
      if (!isOpen || !modalRef.current) return;
      
      // Check if the wheel event is within the modal
      const modal = modalRef.current;
      const scrollContainer = scrollContainerRef.current;
      
      if (scrollContainer && modal.contains(e.target as Node)) {
        // Allow scrolling within modal
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        const isAtTop = scrollTop === 0;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight;
        
        // Prevent background scroll when at boundaries
        if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
          e.preventDefault();
          e.stopPropagation();
        }
      } else {
        // Prevent all background scrolling when modal is open
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Handle touch events to prevent background scrolling
    const handleTouchMove = (e: TouchEvent) => {
      if (!isOpen || !modalRef.current) return;
      if (!modalRef.current.contains(e.target as Node)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown, true);
      document.addEventListener('wheel', handleWheel, { passive: false, capture: true });
      document.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
      
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      if (isTV) {
        setTimeout(() => {
          closeButtonRef.current?.focus();
        }, 300);
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('wheel', handleWheel, true);
      document.removeEventListener('touchmove', handleTouchMove, true);
      
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    };
  }, [isOpen, onClose, isTV]);

  // Load YouTube IFrame API script
  useEffect(() => {
    if (window.YT) {
      setYoutubeApiLoaded(true);
      return;
    }
    
    // Set up global callback for YouTube API
    window.onYouTubeIframeAPIReady = () => {
      console.log('🎬 Modal: YouTube IFrame API loaded successfully');
      setYoutubeApiLoaded(true);
    };
    
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    document.head.appendChild(script);
    
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // Helper function to create YouTube player
  const createYouTubePlayer = useCallback((elementId: string, videoId: string) => {
    // Destroy existing player if any
    if (youtubePlayerRef.current) {
      console.log('🗑️ Modal: Destroying existing player');
      youtubePlayerRef.current.destroy();
      youtubePlayerRef.current = null;
    }
    
    // Create new player
    const player = new window.YT.Player(elementId, {
      videoId: videoId,
      playerVars: {
        autoplay: 1,
        mute: 0,
        controls: 1,
        start: 0,
        loop: 1,
        playlist: videoId,
        showinfo: 0,
        rel: 0,
        modestbranding: 1
      },
      events: {
        onReady: (event: any) => {
          console.log('🎬 Modal: YouTube player ready (new video)!', {
            playerId: event.target.getVideoData().video_id,
            duration: event.target.getDuration()
          });
          handlePlayerReady(event);
        },
        onStateChange: handlePlayerStateChange,
        onError: handlePlayerError
      }
    });
    
    console.log('✅ Modal: YouTube player created successfully');
  }, [handlePlayerReady, handlePlayerStateChange, handlePlayerError]);

  // Initialize YouTube player when trailer video ID changes
  useEffect(() => {
    console.log('🎬 Modal: YouTube player initialization effect triggered', {
      trailerVideoId,
      hasYT: !!window.YT,
      youtubeApiLoaded,
      showTrailer,
      trailerPlayError,
      isOpen,
      movieTitle: movie?.title,
      movieId: movie?.id
    });
    
    if (!trailerVideoId || !isOpen) {
      console.log('❌ Modal: No trailer video ID or modal not open');
      return;
    }
    
    if (!window.YT || !youtubeApiLoaded) {
      console.log('❌ Modal: YouTube API not loaded yet');
      return;
    }
    
    const elementId = `youtube-player-modal-${trailerVideoId}`;
    const element = document.getElementById(elementId);
    if (!element) {
      console.log('❌ Modal: YouTube player element not found:', elementId, '- polling for element');
      // Poll for element with exponential backoff (max 2 seconds)
      let attempts = 0;
      const maxAttempts = 20; // 20 * 100ms = 2 seconds max
      
      const pollForElement = () => {
        const retryElement = document.getElementById(elementId);
        if (retryElement) {
          console.log(`✅ Modal: Element found after ${attempts + 1} attempts, creating player`);
          createYouTubePlayer(elementId, trailerVideoId);
        } else if (attempts < maxAttempts) {
          attempts++;
          const timeout = Math.min(100 * attempts, 500); // Exponential backoff, max 500ms
          setTimeout(pollForElement, timeout);
        } else {
          console.log('❌ Modal: Element still not found after maximum attempts');
          setTrailerPlayError(true);
        }
      };
      
      setTimeout(pollForElement, 50); // Start with 50ms
      return;
    }
    
    console.log('✅ Modal: Creating YouTube player for:', trailerVideoId);
    createYouTubePlayer(elementId, trailerVideoId);
  }, [trailerVideoId, youtubeApiLoaded, trailerPlayError, isOpen, createYouTubePlayer]);

  // Delay timer for trailer playback
  useEffect(() => {
    if (trailerVideoId && isOpen) {
      setIsTrailerLoading(true);
      setTrailerPlayError(false);
      
      const timer = setTimeout(() => {
        setShowTrailer(true);
        // Don't set loading to false here - let the player ready handler do it
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [trailerVideoId, isOpen]);

  // Cleanup YouTube player when modal closes
  useEffect(() => {
    if (!isOpen && youtubePlayerRef.current) {
      console.log('🗑️ Modal: Cleaning up YouTube player on modal close');
      youtubePlayerRef.current.destroy();
      youtubePlayerRef.current = null;
      setShowTrailer(false);
      setIsTrailerLoading(false);
    }
  }, [isOpen]);

  // Memoize image URLs to prevent unnecessary recalculations
  const backdropUrl = useMemo(() => {
    if (!movie) return '/placeholder-movie.png';
    return getImageUrl(movie.backdrop_path, IMAGE_SIZES.backdrop.large);
  }, [movie?.backdrop_path]);
  
  const posterUrl = useMemo(() => {
    if (!movie) return '/placeholder-movie.png';
    return getImageUrl(movie.poster_path, IMAGE_SIZES.poster.large);
  }, [movie?.poster_path]);
  
  const year = movie?.release_date ? new Date(movie.release_date).getFullYear() : 'N/A';
  const rating = movie?.vote_average ? movie.vote_average.toFixed(1) : 'N/A';

  if (!movie) return null;

  const handleSelectAndPlay = (selectedMovie: Movie) => {
    setShowTrailer(false); // Pause trailer before closing
    onSelectVersion(selectedMovie);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={handleBackdropClick}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
          
          {/* Modal Content */}
          <motion.div
            ref={modalRef}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className={`relative max-w-5xl w-[calc(100vw-2rem)] sm:w-[90vw] max-h-[90vh] bg-[#181818] rounded-xl overflow-hidden shadow-2xl ${isTV ? 'tv-modal' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            {/* Backdrop image with gradient - Fixed height container */}
            <div className="modal-backdrop-container relative w-full h-[40vh] lg:h-[50vh]">
              {/* Always render YouTube player div when we have trailerVideoId, but control visibility */}
              {trailerVideoId && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: showTrailer ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="modal-trailer-container w-full h-full relative overflow-hidden"
                  style={{ display: showTrailer ? 'block' : 'none' }}
                >
                  <div
                    id={`youtube-player-modal-${trailerVideoId}`}
                    ref={(el) => {
                      // Trigger player creation when element is mounted
                      if (el && trailerVideoId) {
                        console.log('🎬 Modal YouTube element mounted, notifying hook');
                      }
                    }}
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                    style={{
                      width: '100%',
                      height: '100%',
                      minWidth: '100%',
                      minHeight: '100%'
                    }}
                  />
                  
                  {/* Fallback play button if YouTube player fails */}
                  {trailerPlayError && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <button
                        onClick={() => {
                          // Open trailer in new tab as fallback
                          window.open(`https://www.youtube.com/watch?v=${trailerVideoId}`, '_blank');
                        }}
                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors focus:ring-4 focus:ring-red-600/50"
                        tabIndex={0}
                      >
                        Play Trailer on YouTube
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
              
              {/* Show backdrop image when trailer is not showing */}
              {(!showTrailer || !trailerVideoId) && (
                <motion.img
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  src={backdropError ? '/placeholder-movie.png' : backdropUrl}
                  alt={movie.title}
                  className="w-full h-full object-cover"
                  onError={() => {
                    if (!backdropError) {
                      console.error('Failed to load backdrop for:', movie.title);
                      setBackdropError(true);
                    }
                  }}
                />
              )}
              
              {/* Loading indicator for trailer */}
              {isTrailerLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="absolute inset-0 bg-black/50 flex items-center justify-center"
                >
                  <div className="loading-spinner"></div>
                </motion.div>
              )}
              
              <div className="absolute inset-0 bg-gradient-to-t from-[#181818] via-[#181818]/50 to-transparent pointer-events-none" />
              
              {/* Close button */}
              <button
                ref={closeButtonRef}
                onClick={onClose}
                tabIndex={isTV ? 0 : undefined}
                className={`absolute top-4 right-4 p-2 rounded-full bg-[#181818] hover:bg-[#282828] transition-colors z-10 ${isTV ? 'focus:ring-4 focus:ring-red-600' : ''}`}
                aria-label="Close modal"
                title="Close (or press Back)"
              >
                <X className={`${isTV ? 'w-8 h-8' : 'w-6 h-6'}`} aria-hidden="true" />
              </button>


              {/* Title and main info overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-10">
                <h1
                  id="modal-title"
                  className={`${isTV ? 'text-4xl lg:text-6xl' : 'text-3xl lg:text-5xl'} font-bold mb-4`}
                >
                  {movie.title}
                </h1>

                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-400 fill-current" />
                    <span className="font-semibold text-lg">{rating}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <span>{year}</span>
                  </div>
                  <Badge className="bg-white/20 hover:bg-white/30">HD</Badge>
                </div>
              </div>
            </div>

            {/* Content */}
            <div 
              ref={scrollContainerRef}
              className={`p-8 lg:p-12 overflow-y-auto max-h-[50vh] scroll-smooth ${isTV ? 'focus-within:ring-2 focus-within:ring-red-600/50' : ''}`}
            >
              {/* Navigation Loading Overlay */}
              {isNavigatingToStream && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
                >
                  <div className="text-center">
                    <div className="loading-spinner mb-4"></div>
                    <p className="text-white text-lg font-medium">Opening stream...</p>
                    <p className="text-gray-400 text-sm mt-2">Please wait while we prepare your video</p>
                  </div>
                </motion.div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
                {/* Left column: Main content */}
                <div className="lg:col-span-2 space-y-8">
                  {/* Overview - shown first for context */}
                  {movie.overview && (
                    <div className="space-y-3">
                      <p className="text-gray-300 leading-relaxed text-base">
                        {movie.overview}
                      </p>
                    </div>
                  )}

                  {/* Divider */}
                  <div className="border-t border-gray-700/50" />

                  {/* Available Versions Section with Tabs */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-bold">Available Versions</h3>
                      {isSearching && activeTab === 'download' && (
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <div className="loading-spinner w-4 h-4 border-2" />
                          <span>Searching...</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Tab Navigation */}
                    <div className="flex gap-1 bg-gray-800/50 p-1 rounded-lg">
                      <button
                        onClick={() => setActiveTab('stream')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-all ${
                          activeTab === 'stream'
                            ? 'bg-red-600 text-white shadow-lg'
                            : 'text-gray-400 hover:text-white hover:bg-white/10'
                        } ${isTV ? 'focus:ring-4 focus:ring-red-600/50' : ''}`}
                        tabIndex={isTV ? 0 : undefined}
                      >
                        <Monitor className="w-4 h-4" />
                        <span className="font-medium">Stream Sources</span>
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('download');
                          // Trigger search when switching to download tab if not already searched
                          if (!hasSearched && movie) {
                            handleSearchBot();
                          }
                        }}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-all ${
                          activeTab === 'download'
                            ? 'bg-red-600 text-white shadow-lg'
                            : 'text-gray-400 hover:text-white hover:bg-white/10'
                        } ${isTV ? 'focus:ring-4 focus:ring-red-600/50' : ''}`}
                        tabIndex={isTV ? 0 : undefined}
                      >
                        <Download className="w-4 h-4" />
                        <span className="font-medium">Downloadable Sources</span>
                      </button>
                    </div>
                    
                    {/* Tab Content */}
                    {activeTab === 'stream' ? (
                      /* Stream Sources Tab */
                      <div className="space-y-3">
                        {getStreamSources()
                          .filter((source) => {
                            // Hide VidSrc if check indicates it's not available
                            if (source.id === 'vidsrc') {
                              // Show only if available is true, hide if false or null (loading)
                              return vidsrcAvailable === true;
                            }
                            return true;
                          })
                          .map((source, index) => (
                          <div
                            key={source.id}
                            className={`glass-card p-5 rounded-lg cursor-pointer transition-all duration-150 hover:border-red-600/70 hover:translate-x-1 border border-transparent ${isTV ? 'tv-focusable-item' : ''}`}
                            onClick={() => handleStreamSourceClick(source.id)}
                            role="button"
                            tabIndex={0}
                            aria-label={`Stream via ${source.name}`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                handleStreamSourceClick(source.id);
                              }
                            }}
                          >
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-3">
                                  <span className="text-2xl">{source.icon}</span>
                                  <div>
                                    <h4 className="font-semibold text-white">{source.name}</h4>
                                    <p className="text-sm text-gray-400">{source.description}</p>
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <Badge className="bg-green-600 text-white border-green-500">
                                    ⚡ Instant Play
                                  </Badge>
                                  {source.badge && (
                                    <Badge variant="secondary" className="bg-red-600/20 text-red-400 border-red-600/30">
                                      {source.badge}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <Button
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 flex-shrink-0 shadow-lg"
                                aria-label={`Stream via ${source.name}`}
                              >
                                <Play className="w-4 h-4 fill-current" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* Downloadable Sources Tab */
                      <>
                    {isSearching ? (
                      <div className="space-y-3">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="glass-card p-5 rounded-lg">
                            <Skeleton className="h-5 w-3/4 mb-3 bg-gray-700/50" />
                            <div className="flex gap-2">
                              <Skeleton className="h-6 w-16 bg-gray-700/50" />
                              <Skeleton className="h-6 w-16 bg-gray-700/50" />
                              <Skeleton className="h-6 w-16 bg-gray-700/50" />
                              <Skeleton className="h-6 w-16 bg-gray-700/50" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : searchError ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="glass-card p-8 rounded-lg text-center border border-red-600/30"
                      >
                        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                        <p className="text-gray-300 mb-2 font-medium">No Versions Available</p>
                        <p className="text-sm text-gray-400">{searchError}</p>
                        <Button
                          onClick={handleSearchBot}
                          variant="outline"
                          className="mt-6 border-gray-600 hover:bg-white/5"
                        >
                          Try Again
                        </Button>
                      </motion.div>
                    ) : botResults.length > 0 ? (
                      <div className="space-y-3">
                        {botResults.map((result, index) => (
                          <div
                            key={result.id}
                            className={`glass-card p-5 rounded-lg cursor-pointer transition-all duration-150 hover:border-red-600/70 hover:translate-x-1 border border-transparent ${isTV ? 'tv-focusable-item' : ''}`}
                            onClick={() => handleSelectAndPlay(result)}
                            role="button"
                            tabIndex={0}
                            aria-label={`Play ${result.title}`}
                            data-version-item
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSelectAndPlay(result);
                              }
                            }}
                          >
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold mb-3 text-white truncate">{result.title}</h4>
                                <div className="flex flex-wrap gap-2">
                                  {result.cached && (
                                    <Badge className="bg-green-600 text-white border-green-500">
                                      ⚡ CACHED - Instant Play
                                    </Badge>
                                  )}
                                  <Badge variant="secondary" className="bg-red-600/20 text-red-400 border-red-600/30">
                                    {result.quality}
                                  </Badge>
                                  <Badge variant="secondary" className="bg-blue-600/20 text-blue-400 border-blue-600/30">
                                    {result.size}
                                  </Badge>
                                  <Badge variant="secondary" className="bg-green-600/20 text-green-400 border-green-600/30">
                                    {result.format}
                                  </Badge>
                                  <Badge variant="secondary" className="bg-purple-600/20 text-purple-400 border-purple-600/30">
                                    {result.year}
                                  </Badge>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 flex-shrink-0 shadow-lg"
                                aria-label="Play this version"
                              >
                                <Play className="w-4 h-4 fill-current" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                      </>
                    )}

                  </div>
                </div>

                {/* Right column: Poster and metadata */}
                <div className="space-y-6 hidden lg:block">
                  <img
                    src={posterError ? '/placeholder-movie.png' : posterUrl}
                    alt={movie.title}
                    className="w-full rounded-xl shadow-2xl ring-1 ring-white/10"
                    onError={() => {
                      if (!posterError) {
                        console.error('Failed to load poster for:', movie.title);
                        setPosterError(true);
                      }
                    }}
                  />
                  
                  <div className="space-y-4 text-sm bg-gray-900/50 rounded-xl p-5 border border-gray-800/50">
                    <h4 className="font-bold text-base text-white mb-4">Movie Details</h4>
                    <div className="space-y-3">
                      <div>
                        <span className="text-gray-400 block mb-1">Original Title</span>
                        <p className="font-medium text-white">{movie.original_title}</p>
                      </div>
                      <div className="border-t border-gray-800/50 pt-3">
                        <span className="text-gray-400 block mb-1">Release Date</span>
                        <p className="font-medium text-white">{movie.release_date || 'N/A'}</p>
                      </div>
                      <div className="border-t border-gray-800/50 pt-3">
                        <span className="text-gray-400 block mb-1">Rating</span>
                        <div className="flex items-center gap-2">
                          <Star className="w-4 h-4 text-yellow-400 fill-current" />
                          <p className="font-medium text-white">{rating} / 10</p>
                        </div>
                      </div>
                      <div className="border-t border-gray-800/50 pt-3">
                        <span className="text-gray-400 block mb-1">Popularity</span>
                        <p className="font-medium text-white">{movie.popularity.toFixed(0)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* More Like This Section */}
              {(similarMovies.length > 0 || loadingSimilar) && (
                <div className="mt-12 pt-8 border-t border-gray-700/50">
                  <h3 className="text-2xl font-bold mb-6">More Like This</h3>
                  
                  {loadingSimilar ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="space-y-2">
                          <Skeleton className="aspect-[2/3] w-full rounded-lg bg-gray-700/50" />
                          <Skeleton className="h-4 w-full bg-gray-700/50" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div 
                      className={`grid gap-4 ${isTV ? 'grid-cols-3 lg:grid-cols-4 xl:grid-cols-5' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6'}`}
                      data-similar-movies
                    >
                      {similarMovies.map((similarMovie) => (
                        <div
                          key={`${movieId}-similar-${similarMovie.id}`}
                          className={`cursor-pointer group transition-transform duration-150 hover:scale-105 ${isTV ? 'tv-focusable-item' : ''}`}
                          onClick={() => onMovieClick?.(similarMovie)}
                          role="button"
                          tabIndex={0}
                          aria-label={`View details for ${similarMovie.title}`}
                          data-similar-movie
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              onMovieClick?.(similarMovie);
                            }
                          }}
                        >
                          <div className="relative aspect-[2/3] rounded-lg overflow-hidden shadow-lg">
                            <img
                              src={getImageUrl(similarMovie.poster_path, IMAGE_SIZES.poster.medium)}
                              alt={similarMovie.title}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                              loading="lazy"
                              decoding="async"
                              onError={(e) => {
                                if (!e.currentTarget.src.includes('placeholder-movie.png')) {
                                  e.currentTarget.src = '/placeholder-movie.png';
                                }
                              }}
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                              <Play className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 fill-current" />
                            </div>
                          </div>
                          <h4 className="text-sm font-medium mt-2 text-gray-300 group-hover:text-white transition-colors line-clamp-2">
                            {similarMovie.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <Star className="w-3 h-3 text-yellow-400 fill-current" />
                            <span className="text-xs text-gray-400">
                              {similarMovie.vote_average ? similarMovie.vote_average.toFixed(1) : 'N/A'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}